import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { PageLoader } from "@/components/common";
import { ArrowLeft, Smartphone, Clock } from "lucide-react";

export default function Pay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [network, setNetwork] = useState("MTN");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: config } = useQuery({ queryKey: ["payments-config"], queryFn: () => apiFetch("/payments/config") });
  const { data: listing, isLoading } = useQuery({ queryKey: ["listing", id], queryFn: () => apiFetch(`/listings/${id}`) });

  if (isLoading) return <PageLoader />;

  const pay = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/payments/initiate", { method: "POST", body: JSON.stringify({ listing_id: id, network, phone_number: phone.trim() }) });
      toast.success(res.message || "Payment request sent — approve on your phone");
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-8 sm:px-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full p-1.5 hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl font-extrabold">Pay with Mobile Money</h1>
      </div>

      <div className="mt-4 rounded-2xl border border-border p-4">
        <p className="text-sm text-muted-foreground">{listing.title}</p>
        <p className="text-2xl font-extrabold text-price">{formatMoney(listing.price, listing.currency)}</p>
      </div>

      {!config?.enabled ? (
        <div className="mt-6 flex flex-col items-center gap-2 rounded-2xl border border-border bg-secondary p-8 text-center">
          <Clock className="h-8 w-8 text-muted-foreground" />
          <p className="font-semibold">Mobile Money coming soon</p>
          <p className="text-sm text-muted-foreground">Online payment isn't available yet. For now, arrange Cash or Mobile Money directly with the seller via chat.</p>
        </div>
      ) : listing.currency !== "ZMW" ? (
        <div className="mt-6 rounded-2xl border border-border bg-secondary p-6 text-center text-sm text-muted-foreground">Mobile Money checkout is only available for items priced in Kwacha (ZMW).</div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["MTN", "Airtel"].map((n) => (
              <button key={n} onClick={() => setNetwork(n)} className={`rounded-xl border py-3 text-sm font-semibold ${network === n ? "border-price bg-price-soft text-price" : "border-border"}`}>{n}</button>
            ))}
          </div>
          <div className="relative">
            <Smartphone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="09XXXXXXXX" className="w-full rounded-xl border border-input py-2.5 pl-9 pr-4 text-sm outline-none focus:border-price" data-testid="pay-phone" />
          </div>
          <button onClick={pay} disabled={busy || phone.length < 9} className="w-full rounded-xl bg-price py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="pay-btn">{busy ? "Processing…" : `Pay ${formatMoney(listing.price, listing.currency)}`}</button>
        </div>
      )}
    </div>
  );
}
